// components/QuickChatTerminal.js
import React, { useEffect, useMemo, useRef, useState } from "react";

const QuickChatTerminal = () => {
  const terminalBodyRef = useRef(null);
  const inputRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [lines, setLines] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(null);

  const prompt = "williamwritescode@admin:~$";

  const commands = useMemo(
    () => ({
      "/help": {
        title: "Available Commands",
        content: [
          "Use any of the commands below to quickly explore William's portfolio:",
          "",
          "/about              Brief professional introduction",
          "/skills             Technical skills and tools",
          "/services           Services William offers",
          "/experience         Professional experience summary",
          "/projects           Selected project highlights",
          "/pricing            Payment structure and pricing model",
          "/education          Education background",
          "/certifications     Certifications and learning focus",
          "/contact            Contact details",
          "/profile            Public profile links and verified GitHub data",
          "/vision             Career and business vision",
          "/name               Full name and brand",
          "/clear              Clear the terminal",
        ],
      },

      "/about": {
        title: "About William",
        content: [
          "William Sisulu Odhiambo is a full-stack software engineer, cybersecurity specialist, and founder building practical digital systems for businesses, startups, and communities.",
          "",
          "He works across software development, cybersecurity, automation, analytics, and product strategy, with a strong focus on Africa-first technology solutions.",
        ],
      },

      "/skills": {
        title: "Skills",
        content: [
          "Frontend:",
          "React, React Native, TailwindCSS, JavaScript, TypeScript, responsive UI implementation, dashboard interfaces, and interactive animations.",
          "",
          "Backend:",
          "Node.js, Express.js, MongoDB, Mongoose, REST APIs, authentication, authorization, payment integration, and deployment workflows.",
          "",
          "Cybersecurity:",
          "Security awareness, system hardening, risk analysis, secure authentication, threat monitoring concepts, vulnerability thinking, and defensive architecture.",
          "",
          "Tools:",
          "Git, GitHub, VPS/Linux, PM2, Nginx, Cloudinary, PayPal, M-Pesa Daraja API, Firebase basics, Figma, and automation tools.",
        ],
      },

      "/services": {
        title: "Services",
        content: [
          "William offers:",
          "",
          "01. Full-stack web application development",
          "02. Mobile app development",
          "03. Backend API development",
          "04. Admin dashboards and business systems",
          "05. E-commerce and marketplace platforms",
          "06. Payment integrations such as PayPal and M-Pesa",
          "07. Cybersecurity consultation and secure system planning",
          "08. Maintenance, optimization, and technical support",
          "09. Technical consultation for founders, SMEs, and startups",
        ],
      },

      "/experience": {
        title: "Professional Experience",
        content: [
          "Founder — SeptaGreen Africa",
          "Leading a technology and cybersecurity-focused company built around cybersecurity, software solutions, and data science/analytics.",
          "",
          "Full Stack Software Engineer & Robotics Trainer — Afribot Robotics Africa Limited",
          "Worked on software development, robotics training, frontend/backend systems, and technical instruction.",
          "",
          "Software Developer Volunteer — IOME001",
          "Supported software and hardware-based innovation work involving Raspberry Pi, Arduino, and scientific instrumentation.",
        ],
      },

      "/projects": {
        title: "Selected Projects",
        content: [
          "Some selected works and project directions include:",
          "",
          "Asiyo Women Connect — community and marketplace ecosystem.",
          "Zivuko Kenya — e-commerce/marketplace platform.",
          "SeptaGreen Africa — technology and cybersecurity company.",
          "Emberprise Africa — business toolkit and intelligence platform.",
          "Longclause — contract and legal-tech concept for freelancers and creatives.",
          "Bookie LMS — learning management system.",
          "Remunary — POS/billing and retail management concept.",
          "",
          "Use the full website sections to explore deeper details and visuals.",
        ],
      },

      "/pricing": {
        title: "Pricing",
        content: [
          "Pricing depends on project scope, complexity, timeline, integrations, and deliverables.",
          "",
          "Monthly Payment Plan:",
          "Payments are made monthly, due by the 10th of each month. The first monthly payment acts as the project deposit before work begins.",
          "",
          "One-Time Payment Plan:",
          "A 50% deposit is required before work begins, with the remaining balance paid on completion or an agreed milestone.",
          "",
          "Maintenance and consultation plans can also be billed monthly in Kenyan Shillings.",
        ],
      },

      "/education": {
        title: "Education",
        content: [
          "William's learning path is centered around software engineering, cybersecurity, cloud computing, data science, robotics, and practical system building.",
          "",
          "His work combines academic learning, self-driven technical practice, client projects, and startup development experience.",
        ],
      },

      "/certifications": {
        title: "Certifications",
        content: [
          "Certification and learning focus areas include:",
          "",
          "Cybersecurity",
          "Cloud Computing",
          "Data Science",
          "Software Engineering",
          "Robotics and automation",
          "Backend/API development",
          "Secure system design",
          "",
          "The full certifications section gives a deeper view of the learning path.",
        ],
      },

      "/contact": {
        title: "Contact",
        content: [
          "Email: williamsisulu2003@gmail.com or info@williamwritescode.com",
          "Phone: +254 711 160 437",
          "Location: Kenya",
          "GitHub: https://github.com/i-william17",
          "LinkedIn: https://www.linkedin.com/in/william-odhiambo-481689265/",
          "X/Twitter: https://x.com/talesofwilliam",
          "",
          "You can also use the Contact page to send a message, schedule a consultation, or reach out via WhatsApp.",
        ],
      },

      "/profile": {
        title: "Verified Public Profiles",
        content: [
          "GitHub: William Odhiambo (@i-william17)",
          "Bio: A work in progress.",
          "Location: Kenya",
          "Open to work: Yes",
          "Public repositories: 58",
          "X/Twitter: @talesofwilliam",
          "LinkedIn: https://www.linkedin.com/in/william-odhiambo-481689265/",
        ],
      },

      "/vision": {
        title: "Vision",
        content: [
          "William's vision is to build reliable, secure, and scalable technology that helps people, startups, SMEs, and African communities move forward.",
          "",
          "The long-term direction is to create Africa-first digital products that can scale globally while remaining practical, accessible, and impactful.",
        ],
      },

      "/name": {
        title: "Identity",
        content: [
          "Name: William Sisulu Odhiambo",
          "Brand: William Writes Code",
          "Positioning: Full-stack software engineer, cybersecurity specialist, founder, and builder.",
        ],
      },
    }),
    []
  );

  const quickCommands = [
    "/help",
    "/skills",
    "/experience",
    "/projects",
    "/services",
    "/pricing",
    "/contact",
    "/profile",
    "/vision",
  ];

  const typeResponse = async (responseLines) => {
    setIsTyping(true);

    for (let i = 0; i < responseLines.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 120));

      setLines((prev) => [
        ...prev,
        {
          type: "response",
          text: responseLines[i],
        },
      ]);
    }

    setIsTyping(false);
  };

  const runCommand = async (rawCommand) => {
    const command = rawCommand.trim().toLowerCase();

    if (!command) return;

    setLines((prev) => [
      ...prev,
      {
        type: "command",
        text: command,
      },
    ]);

    setHistory((prev) => [...prev, command]);
    setHistoryIndex(null);
    setInputValue("");

    if (command === "/clear" || command === "clear") {
      setTimeout(() => {
        setLines([]);
      }, 120);
      return;
    }

    const normalizedCommand = command.startsWith("/")
      ? command
      : `/${command}`;

    if (!commands[normalizedCommand]) {
      await typeResponse([
        `Command not found: ${command}`,
        "Type /help to view available commands.",
      ]);
      return;
    }

    const selected = commands[normalizedCommand];

    await typeResponse([
      `> ${selected.title}`,
      "----------------------------------------",
      ...selected.content,
      "",
    ]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isTyping) return;

    runCommand(inputValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();

      if (history.length === 0) return;

      const nextIndex =
        historyIndex === null
          ? history.length - 1
          : Math.max(historyIndex - 1, 0);

      setHistoryIndex(nextIndex);
      setInputValue(history[nextIndex]);
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (history.length === 0 || historyIndex === null) return;

      const nextIndex = historyIndex + 1;

      if (nextIndex >= history.length) {
        setHistoryIndex(null);
        setInputValue("");
        return;
      }

      setHistoryIndex(nextIndex);
      setInputValue(history[nextIndex]);
    }
  };

  useEffect(() => {
    const initialLines = [
      {
        type: "system",
        text: "Booting William Writes Code quick interface...",
      },
      {
        type: "system",
        text: "Type /help to view commands or try /skills, /projects, /pricing, /contact, /profile.",
      },
    ];

    const timer = setTimeout(() => {
      setLines(initialLines);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!terminalBodyRef.current) return;

    terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
  }, [lines, isTyping, inputValue]);

  return (
    <section
      id="quick-chat"
      className="relative overflow-hidden border-t border-black/10 bg-[#f4f3ee] py-16 text-[#242424] dark:border-white/10 dark:bg-black dark:text-white sm:py-20 md:py-24"
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6">
        <div className="grid items-center gap-8 sm:gap-10 lg:grid-cols-[0.4fr_0.6fr] lg:gap-16 xl:gap-20">
          <div className="min-w-0">
            <h2 className="max-w-3xl text-[clamp(2.8rem,7vw,6.6rem)] font-normal leading-[0.9] tracking-[-0.07em] text-[#242424] dark:text-white">
              Ask the portfolio{" "}
              <span className="editorial-italic brand-accent">directly.</span>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-snug tracking-[-0.025em] text-[#5f5b54] dark:text-[#c8c3b8] sm:text-lg">
              Use short commands to jump into skills, projects, services,
              pricing, experience, education, certifications, and contact.
            </p>
          </div>

          <div className="relative min-w-0">
            <div className="relative overflow-hidden rounded-lg border border-black/10 bg-[#111] dark:border-white/12">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.04] px-3 py-3 sm:px-4">
                <div className="flex shrink-0 items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-white/55" />
                  <span className="h-2 w-2 rounded-full bg-white/35" />
                  <span className="h-2 w-2 rounded-full bg-white/20" />
                </div>

                <div className="min-w-0 truncate text-[10px] font-medium uppercase tracking-[0.2em] text-gray-400 sm:text-xs">
                  @william-writes-code
                </div>

                <button
                  type="button"
                  onClick={() => runCommand("/clear")}
                  className="shrink-0 rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-300 transition hover:border-white/40 hover:text-white sm:px-3 sm:text-xs"
                >
                  clear
                </button>
              </div>

              <div
                ref={terminalBodyRef}
                onClick={() => inputRef.current?.focus()}
                className="h-[430px] cursor-text overflow-y-auto px-3 py-4 font-mono text-[12px] leading-relaxed text-gray-200 sm:h-[500px] sm:px-5 sm:py-5 sm:text-sm md:h-[560px] md:px-6"
              >
                <div className="space-y-2">
                  {lines.map((line, index) => {
                    if (line.type === "command") {
                      return (
                        <div
                          key={index}
                          className="flex min-w-0 flex-wrap gap-x-2 gap-y-1"
                        >
                          <span className="break-all text-[#d7d1c4]">
                            {prompt}
                          </span>
                          <span className="break-all text-white">
                            {line.text}
                          </span>
                        </div>
                      );
                    }

                    if (line.type === "system") {
                      return (
                        <div key={index} className="break-words text-[#b9b4aa]">
                          {line.text}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={index}
                        className={`whitespace-pre-wrap break-words ${
                          line.text.startsWith(">")
                            ? "pt-3 font-bold text-[#f4f3ee]"
                            : line.text.startsWith("---")
                            ? "text-gray-600"
                            : line.text === ""
                            ? "h-2"
                            : "text-gray-300"
                        }`}
                      >
                        {line.text}
                      </div>
                    );
                  })}

                  {isTyping && (
                    <div className="flex items-center gap-2 text-[#d7d1c4]">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-[#d7d1c4]" />
                      <span>generating response...</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="pt-2">
                    <div className="flex min-w-0 items-start gap-2 sm:items-center">
                      <span className="hidden shrink-0 break-all text-[#d7d1c4] sm:inline">
                        {prompt}
                      </span>

                      <span className="shrink-0 text-[#d7d1c4] sm:hidden">
                        $
                      </span>

                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        disabled={isTyping}
                        onChange={(event) => setInputValue(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type /help..."
                        className="min-w-0 flex-1 bg-transparent font-mono text-[12px] text-white outline-none placeholder:text-gray-600 disabled:cursor-not-allowed sm:text-sm"
                        autoComplete="off"
                      />

                      <button
                        type="submit"
                        disabled={isTyping || !inputValue.trim()}
                        className="sr-only"
                        aria-label="Run command"
                      >
                        Run
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="border-t border-white/10 bg-white/[0.03] px-3 py-3 sm:px-5 sm:py-4 md:px-6">
                <div className="mb-3 flex max-h-24 flex-wrap gap-2 overflow-y-auto pr-1 sm:mb-4 sm:max-h-none sm:overflow-visible">
                  {quickCommands.map((command) => (
                    <button
                      key={command}
                      type="button"
                      disabled={isTyping}
                      onClick={() => runCommand(command)}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1.5 font-mono text-[10px] text-gray-300 transition hover:border-white/35 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 sm:text-xs"
                    >
                      {command}
                    </button>
                  ))}
                </div>

                <p className="mt-2 text-[10px] leading-relaxed text-gray-500 sm:mt-3 sm:text-xs">
                  Tip: use Arrow Up / Arrow Down to navigate command history.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickChatTerminal;
